import { useEffect, useState, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Layout, Search, ImageIcon, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import DrillBoardDialog from "../components/trainings/DrillBoardDialog";
import DrillDiagramPreview from "../components/trainings/DrillDiagramPreview";
import PublicLibraryDialog from "../components/trainings/PublicLibraryDialog";

export const CATEGORIES = [
  "Riscaldamento",
  "Possesso palla",
  "Transizioni",
  "Finalizzazione",
  "Difesa",
  "1 vs 1",
  "Atletico",
  "Partita a tema",
];

const empty = { name: "", category: "", duration: 15, description: "", diagram: null, image_url: null };

export default function ExerciseLibraryPage() {
  const { activeTeam, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");
  const [publicOpen, setPublicOpen] = useState(false);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/exercises`);
    setItems(data);
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const boardDrill = { name: form.name, diagram: form.diagram };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Nome esercizio richiesto");
      return;
    }
    const payload = {
      team_id: activeTeam.id,
      name: form.name,
      category: form.category || null,
      duration: Number(form.duration) || 15,
      description: form.description,
      diagram: form.diagram,
      image_url: form.image_url || null,
    };
    if (editingId) await api.put(`/exercises/${editingId}`, payload);
    else await api.post(`/exercises`, payload);
    toast.success("Esercizio salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const handleEdit = (e) => {
    setForm({
      name: e.name,
      category: e.category || "",
      duration: e.duration || 15,
      description: e.description || "",
      diagram: e.diagram || null,
      image_url: e.image_url || null,
    });
    setEditingId(e.id);
    setOpen(true);
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`Eliminare "${e.name}"?`)) return;
    await api.delete(`/exercises/${e.id}`);
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    }
  };

  const saveBoard = (diagram) => setForm({ ...form, diagram });

  const filtered = items.filter((e) => {
    const matchesSearch = `${e.name} ${e.category || ""} ${e.description || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesCat = filterCat === "__all__" || e.category === filterCat;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Libreria esercizi</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Archivio drills</h1>
          <p className="text-slate-500">
            {filtered.length} esercizi · riutilizzabili nei piani allenamento
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-56"
              data-testid="library-search"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Tutte categorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte le categorie</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button
              onClick={() => setPublicOpen(true)}
              variant="outline"
              className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="open-public-library-btn"
            >
              <BookOpen size={16} className="mr-1" /> Libreria pubblica
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={handleNew}
              className="rounded-full bg-sky-500 hover:bg-sky-600"
              data-testid="new-exercise-btn"
            >
              <Plus size={16} className="mr-1" /> Nuovo
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((e) => (
          <Card
            key={e.id}
            className="border-slate-200 overflow-hidden hover:shadow-md transition flex flex-col"
            data-testid={`exercise-${e.id}`}
          >
            {e.image_url ? (
              <img
                src={e.image_url}
                alt={e.name}
                className="w-full h-32 object-cover bg-slate-100"
              />
            ) : (
              <DrillDiagramPreview diagram={e.diagram} />
            )}
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold truncate">{e.name}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    {e.category && (
                      <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-medium">
                        {e.category}
                      </span>
                    )}
                    <span>{e.duration || 15} min</span>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-0.5 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(e)}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(e)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
              {e.description && (
                <p className="text-xs text-slate-500 mt-2 line-clamp-3">{e.description}</p>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
            {items.length === 0
              ? "Nessun esercizio in libreria. Aggiungi il primo!"
              : "Nessun esercizio corrisponde ai filtri."}
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica esercizio" : "Nuovo esercizio"}</DialogTitle>
          <DialogDescription className="sr-only">Inserisci o modifica un esercizio della libreria.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome esercizio</Label>
              <Input
                value={form.name}
                onChange={(ev) => setForm({ ...form, name: ev.target.value })}
                placeholder="Rondo 5v2"
                data-testid="exercise-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select
                  value={form.category || "__none__"}
                  onValueChange={(v) =>
                    setForm({ ...form, category: v === "__none__" ? "" : v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Senza categoria —</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durata (min)</Label>
                <Input
                  type="number"
                  value={form.duration}
                  onChange={(ev) => setForm({ ...form, duration: ev.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(ev) => setForm({ ...form, description: ev.target.value })}
                placeholder="Obiettivi, ripetizioni, regole..."
              />
            </div>
            <div>
              <Label className="mb-2 block">Diagramma</Label>
              <DrillDiagramPreview diagram={form.diagram} className="mb-2" />
              <Button
                type="button"
                variant="outline"
                onClick={() => setBoardOpen(true)}
                data-testid="open-board-from-library"
              >
                <Layout size={14} className="mr-1" /> {form.diagram ? "Modifica" : "Disegna"} diagramma
              </Button>
            </div>

            <div>
              <Label className="mb-2 block">Immagine di riferimento (opzionale)</Label>
              {form.image_url && (
                <div className="mb-2 relative inline-block">
                  <img
                    src={form.image_url}
                    alt="esercizio"
                    className="max-h-32 rounded-lg border border-slate-200 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: null })}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-red-50 hover:border-red-200"
                    title="Rimuovi foto"
                  >
                    <X size={12} className="text-red-500" />
                  </button>
                </div>
              )}
              <label
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-medium cursor-pointer hover:bg-slate-50"
                data-testid="exercise-image-upload"
              >
                <ImageIcon size={14} /> {form.image_url ? "Cambia immagine" : "Carica immagine"}
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    uploadImage(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-exercise-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DrillBoardDialog
        open={boardOpen}
        onOpenChange={setBoardOpen}
        drill={boardDrill}
        onSave={saveBoard}
      />

      <PublicLibraryDialog
        open={publicOpen}
        onOpenChange={setPublicOpen}
        teamId={activeTeam?.id}
        onImported={load}
      />
    </div>
  );
}
